'use client';

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { apiFetch } from './api';
import { useAuth } from './auth';

export type LicenseDetail = {
  licenseId: number;
  planCode: string;
  planName: string;
  status: string;
  startsAt: string | null;
  expiresAt: string | null;
  autoRenew: boolean;
  modules: string[];
};

export type UserLicense = {
  hasActiveLicense: boolean;
  licenses: LicenseDetail[];
  modules: string[];
};

type LicenseContextType = {
  license: UserLicense | null;
  loading: boolean;
  refresh: () => void;
  hasModule: (code: string) => boolean;
};

const defaultLicense: UserLicense = {
  hasActiveLicense: false,
  licenses: [],
  modules: [],
};

const LicenseContext = createContext<LicenseContextType>({
  license: defaultLicense,
  loading: false,
  refresh: () => {},
  hasModule: () => false,
});

export function LicenseProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, mounted } = useAuth();
  const [license, setLicense] = useState<UserLicense | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchLicense = useCallback(() => {
    if (!isAuthenticated) {
      setLicense(null);
      return;
    }
    setLoading(true);
    apiFetch<UserLicense>('/api/user/license', { silent: true })
      .then((data) => setLicense(data))
      .catch(() => setLicense(null))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  useEffect(() => {
    if (mounted) {
      fetchLicense();
    }
  }, [mounted, fetchLicense]);

  const hasModule = useCallback(
    (code: string) => license?.modules?.includes(code) ?? false,
    [license]
  );

  return (
    <LicenseContext.Provider value={{ license, loading, refresh: fetchLicense, hasModule }}>
      {children}
    </LicenseContext.Provider>
  );
}

export function useLicense() {
  return useContext(LicenseContext);
}
